import { useQuery } from '@tanstack/react-query';
import { get } from './api';
import { useAuth } from './auth';

export interface Lookups {
  property: {
    code: string;
    tradingName: string;
    legalName: string;
    tin: string | null;
    address: Record<string, string>;
    phone: string | null;
    email: string | null;
    website: string | null;
    timezone: string;
    baseCurrency: string;
    checkInTime: string;
    checkOutTime: string;
    pricesIncludeTaxes: boolean;
    starRating: number | null;
    settings?: { taxesIncludedIn?: string[]; floorNames?: Record<string, string>; onlinePayments?: string[]; operations?: { safe_deposit_boxes?: boolean; night_audit_time?: string }; housekeeping?: { groupSize?: number }; accounts?: { deductWhtFromSuppliers?: boolean } };
  };
  businessDate: string;
  exchangeRates: { currency: string; effectiveDate: string; rate: string }[];
  roomTypes: { id: string; code: string; name: string; category: string; maxAdults: number; maxChildren: number; maxOccupancy: number; sizeSqm: string | null; beds: string | null; amenities: string[]; isActive: boolean; isSellable: boolean; baseOccupancy: number; keysPerUnit: number }[];
  ratePlans: { id: string; code: string; name: string; currency: string; mealPlan: string; requiresCompany: boolean; isZeroRate: boolean; channels: string[]; isActive: boolean; marketSegmentCode: string | null }[];
  transactionCodes: { code: string; name: string; kind: string; category: string; allowManual: boolean; defaultPrice: string | null }[];
  /** viaTerminal: taken on a terminal (card, or a transfer to its account); letGoOnly: the GM's "let go owing", never chosen as a payment. */
  paymentMethods: { code: string; name: string; requiresReference: boolean; requiresVerification: boolean; gateway: string | null; isCityLedger: boolean; viaTerminal: boolean; letGoOnly: boolean }[];
  taxRules: { code: string; name: string; percent: string; appliesTo: string[]; compoundOn: string[]; exemptCodes: string[]; sortOrder: number; isActive: boolean }[];
  marketSegments: { code: string; name: string; groupCode: string }[];
  bookingSources: { code: string; name: string }[];
  vipCodes: { code: string; name: string }[];
  idDocumentTypes: { code: string; name: string; domestic: boolean }[];
  cancellationPolicies: { code: string; name: string }[];
  guaranteePolicies: { code: string; name: string }[];
  departments: { code: string; name: string }[];
  hkSections: { code: string; name: string }[];
  bankAccounts: { code: string; name: string; bankName: string | null; currency: string }[];
  posTerminals: { code: string; location: string; acquirer: string | null; settlesTo: string | null; accountNumber: string | null; terminalId: string | null }[];
  preferences: string[];
  dietary: string[];
  kitchens: { code: string; name: string; outlets: string[] }[];
}

export function useLookups() {
  const { user } = useAuth();
  // The API refuses everything else until a required new password or two-step sign-in is set up: ask only once it will answer.
  const open = !!user && !user.mustChangePassword && !user.mfaSetupRequired;
  return useQuery({ queryKey: ['lookups'], queryFn: () => get<Lookups>('/lookups'), staleTime: 60_000, enabled: open });
}

/** Online payment gateways: display names, in the hotel's order of preference. */
export const GATEWAY_NAMES: Record<string, string> = { MONNIFY: 'Monnify', PAYSTACK: 'Paystack', FLW: 'Flutterwave' };
export function onlineGateways(lookups: Lookups): string[] {
  return lookups.property.settings?.onlinePayments?.length ? lookups.property.settings.onlinePayments : ['MONNIFY'];
}

/** How the hotel names a floor (level 1 is the "Ground floor" at this hotel). */
export function floorName(lookups: Lookups, floor: number): string {
  return lookups.property.settings?.floorNames?.[String(floor)] ?? `Floor ${floor}`;
}

/** Lookups are loaded before the shell renders, so pages can rely on them. */
export function useRefData() {
  const { data } = useLookups();
  if (!data) throw new Error('Lookups not loaded');
  return data;
}

/** The bank accounts naira goes into or out of: the dollar (domiciliary) account takes dollars only (2026-10-16). */
export function nairaBanks(lookups: Lookups) {
  return lookups.bankAccounts.filter((b) => b.currency === lookups.property.baseCurrency);
}

/** Whether the hotel takes withholding tax off supplier payments (owner, 2026-10-15: no, on the tax consultant's advice). */
export function useDeductsWht() {
  const { data } = useLookups();
  return data?.property.settings?.accounts?.deductWhtFromSuppliers === true;
}

/** Whether the front desk keeps safe-deposit boxes (off where every room has its own safe). */
export function useSafeBoxes(): boolean {
  const { data } = useLookups();
  return data?.property.settings?.operations?.safe_deposit_boxes !== false;
}
